import { useState, useEffect, useRef, useCallback } from 'react';
import type { Meeting, ActionItem, AISuggestResponse, AISuggestion, ReportRole, User, ActionItemStatus, ActionItemPriority } from '@/types';

// =============================================================================
// Shared Styles (matching CMRReport watercolor aesthetic)
// =============================================================================

const watercolorBg = (hue: number, sat = "30%", light = "95%") =>
  `radial-gradient(ellipse at 20% 20%, hsla(${hue},${sat},${light},0.6) 0%, transparent 50%),
   radial-gradient(ellipse at 80% 80%, hsla(${hue + 30},${sat},${light},0.4) 0%, transparent 50%),
   radial-gradient(ellipse at 50% 50%, hsla(${hue + 15},20%,98%,1) 0%, hsla(${hue},10%,99%,1) 100%)`;

const cardStyle: React.CSSProperties = {
  background: watercolorBg(220),
  borderRadius: 20,
  padding: '28px 24px',
  boxShadow: '0 2px 20px rgba(100,60,140,0.06)',
  border: '1px solid rgba(180,160,210,0.2)',
  marginBottom: 20,
};

const btnPrimary: React.CSSProperties = {
  padding: '10px 24px', borderRadius: 20, border: 'none', cursor: 'pointer',
  background: 'linear-gradient(135deg, #7c4dba, #9b72cf)', color: '#fff',
  fontSize: 14, fontWeight: 600, transition: 'transform 0.2s, box-shadow 0.2s',
  boxShadow: '0 4px 12px rgba(124,77,186,0.25)',
};

const btnSecondary: React.CSSProperties = {
  padding: '8px 18px', borderRadius: 16, border: '1.5px solid #d4c5e8',
  background: 'rgba(255,255,255,0.6)', color: '#7c4dba', fontSize: 13,
  fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s',
};

const btnDanger: React.CSSProperties = {
  padding: '8px 18px', borderRadius: 16, border: '1.5px solid rgba(220,38,38,0.3)',
  background: 'rgba(220,38,38,0.05)', color: '#dc2626', fontSize: 13,
  fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #d4c5e8',
  fontFamily: "'Georgia', serif", fontSize: 15,
  background: 'rgba(255,255,255,0.7)', boxSizing: 'border-box' as const,
  transition: 'border-color 0.2s',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle, resize: 'vertical' as const, lineHeight: 1.7, minHeight: 100,
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontWeight: 600, color: '#4a3660', fontSize: 14, marginBottom: 6,
};

// =============================================================================
// Status / Priority Helpers
// =============================================================================

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  pending: { label: 'Pending', color: '#b45309', bg: 'rgba(180,83,9,0.08)', icon: '🟡' },
  accepted: { label: 'Accepted', color: '#059669', bg: 'rgba(5,150,105,0.08)', icon: '🟢' },
  declined: { label: 'Declined', color: '#dc2626', bg: 'rgba(220,38,38,0.08)', icon: '🔴' },
  completed: { label: 'Completed', color: '#2563eb', bg: 'rgba(37,99,235,0.08)', icon: '🔵' },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: 'rgba(107,114,128,0.08)', icon: '⚪' },
};

const ACTION_STATUS_CONFIG: Record<ActionItemStatus, { label: string; color: string; bg: string; icon: string }> = {
  not_started: { label: 'Not Started', color: '#6b7280', bg: 'rgba(107,114,128,0.08)', icon: '⬚' },
  in_progress: { label: 'In Progress', color: '#b45309', bg: 'rgba(180,83,9,0.08)', icon: '🔄' },
  completed: { label: 'Completed', color: '#059669', bg: 'rgba(5,150,105,0.08)', icon: '✅' },
  deferred: { label: 'Deferred', color: '#7c3aed', bg: 'rgba(124,58,237,0.08)', icon: '⏸' },
};

const PRIORITY_CONFIG: Record<ActionItemPriority, { label: string; color: string; dot: string }> = {
  high: { label: 'High', color: '#dc2626', dot: '🔴' },
  medium: { label: 'Medium', color: '#b45309', dot: '🟡' },
  low: { label: 'Low', color: '#059669', dot: '🟢' },
};

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    + ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const formatDuration = (mins: number) => {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h} hour${h > 1 ? 's' : ''}`;
};

const isOverdue = (item: ActionItem) => {
  if (!item.dueDate || item.status === 'completed' || item.status === 'deferred') return false;
  return new Date(item.dueDate) < new Date();
};

const isDueSoon = (item: ActionItem) => {
  if (!item.dueDate || item.status === 'completed' || item.status === 'deferred') return false;
  const due = new Date(item.dueDate);
  const now = new Date();
  const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  return due >= now && due <= weekFromNow;
};

// =============================================================================
// Sub-Components
// =============================================================================

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span style={{
      padding: '4px 12px', borderRadius: 12, fontSize: 11, fontWeight: 600,
      color: cfg.color, background: cfg.bg, display: 'inline-flex', alignItems: 'center', gap: 4,
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function ActionStatusDropdown({ status, canEdit, onChange }: {
  status: ActionItemStatus; canEdit: boolean; onChange: (s: ActionItemStatus) => void;
}) {
  const cfg = ACTION_STATUS_CONFIG[status];
  if (!canEdit) {
    return (
      <span style={{
        padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
        color: cfg.color, background: cfg.bg, opacity: 0.8,
      }}>
        {cfg.icon} {cfg.label}
      </span>
    );
  }
  return (
    <select
      value={status}
      onChange={e => onChange(e.target.value as ActionItemStatus)}
      style={{
        padding: '4px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
        color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}33`,
        cursor: 'pointer', outline: 'none',
      }}
    >
      <option value="not_started">⬚ Not Started</option>
      <option value="in_progress">🔄 In Progress</option>
      <option value="completed">✅ Completed</option>
      <option value="deferred">⏸ Deferred</option>
    </select>
  );
}

// =============================================================================
// Meetings Section Props
// =============================================================================

interface MeetingsSectionProps {
  reportId: string;
  user: User;
  role: ReportRole;
  data: Record<string, string>; // report data for sponsor/mentor emails
}

// =============================================================================
// Main Meetings Section Component
// =============================================================================

export default function MeetingsSection({ reportId, user, role, data }: MeetingsSectionProps) {
  // View state
  type ViewState = 
    | { type: 'dashboard' }
    | { type: 'schedule' }
    | { type: 'detail'; meetingId: string };
  
  const [view, setView] = useState<ViewState>({ type: 'dashboard' });
  
  // Data state
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [allActionItems, setAllActionItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load meetings and action items
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [meetingsRes, actionsRes] = await Promise.all([
        fetch(`/api/reports/${reportId}/meetings`, { credentials: 'include' }),
        fetch(`/api/reports/${reportId}/action-items`, { credentials: 'include' }),
      ]);
      if (meetingsRes.ok) {
        const { meetings: m } = await meetingsRes.json();
        setMeetings(m || []);
      }
      if (actionsRes.ok) {
        const { actionItems: a } = await actionsRes.json();
        setAllActionItems(a || []);
      }
    } catch (err) {
      setError('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>📅</div>
        <p style={{ color: '#7a6a8a', fontSize: 15 }}>Loading meetings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <p style={{ color: '#dc2626', fontSize: 15 }}>{error}</p>
        <button onClick={loadData} style={btnSecondary}>Try Again</button>
      </div>
    );
  }

  return (
    <div>
      {view.type === 'dashboard' && (
        <MeetingsDashboard
          meetings={meetings}
          allActionItems={allActionItems}
          user={user}
          role={role}
          reportId={reportId}
          onSchedule={() => setView({ type: 'schedule' })}
          onSelectMeeting={(id) => setView({ type: 'detail', meetingId: id })}
          onRefresh={loadData}
        />
      )}
      {view.type === 'schedule' && (
        <ScheduleMeetingForm
          reportId={reportId}
          user={user}
          role={role}
          data={data}
          onBack={() => setView({ type: 'dashboard' })}
          onCreated={() => { loadData(); setView({ type: 'dashboard' }); }}
        />
      )}
      {view.type === 'detail' && (
        <MeetingDetail
          meetingId={view.meetingId}
          user={user}
          role={role}
          reportId={reportId}
          onBack={() => { loadData(); setView({ type: 'dashboard' }); }}
          onRefresh={loadData}
        />
      )}
    </div>
  );
}

// =============================================================================
// Dashboard View
// =============================================================================

function MeetingsDashboard({ meetings, allActionItems, user, role, reportId, onSchedule, onSelectMeeting, onRefresh }: {
  meetings: Meeting[];
  allActionItems: ActionItem[];
  user: User;
  role: ReportRole;
  reportId: string;
  onSchedule: () => void;
  onSelectMeeting: (id: string) => void;
  onRefresh: () => void;
}) {
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'past' | 'pending'>('all');
  const [showAllActions, setShowAllActions] = useState(false);

  // Filter action items
  const openItems = allActionItems.filter(i => i.status === 'not_started' || i.status === 'in_progress');
  const closedItems = allActionItems.filter(i => i.status === 'completed' || i.status === 'deferred');
  const displayItems = showAllActions ? allActionItems : openItems;

  // Sort: overdue first, then due soon, then rest
  const sortedItems = [...displayItems].sort((a, b) => {
    const aOverdue = isOverdue(a) ? 0 : isDueSoon(a) ? 1 : 2;
    const bOverdue = isOverdue(b) ? 0 : isDueSoon(b) ? 1 : 2;
    return aOverdue - bOverdue;
  });

  // Filter meetings
  const now = new Date();
  const filteredMeetings = meetings.filter(m => {
    if (filter === 'upcoming') return new Date(m.dateTime) >= now && m.status !== 'cancelled';
    if (filter === 'past') return new Date(m.dateTime) < now || m.status === 'completed';
    if (filter === 'pending') return m.status === 'pending';
    return true;
  }).sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());

  // Handle action item status update from dashboard
  const handleStatusUpdate = async (item: ActionItem, newStatus: ActionItemStatus) => {
    try {
      await fetch(`/api/meetings/${item.meetingId}/action-items/${item.actionItemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      onRefresh();
    } catch (err) {
      console.error('Failed to update action item:', err);
    }
  };

  return (
    <>
      {/* Outstanding Action Items Dashboard */}
      {allActionItems.length > 0 && (
        <div style={{ ...cardStyle, background: watercolorBg(190) }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, color: '#3a2a50', fontSize: 18, fontFamily: "'Georgia', serif" }}>
              ⚡ Outstanding Action Items
            </h3>
            <span style={{
              padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600,
              color: openItems.length > 0 ? '#b45309' : '#059669',
              background: openItems.length > 0 ? 'rgba(180,83,9,0.08)' : 'rgba(5,150,105,0.08)',
            }}>
              {openItems.length} open
            </span>
          </div>

          {sortedItems.length === 0 ? (
            <p style={{ color: '#7a6a8a', fontSize: 14, fontStyle: 'italic', textAlign: 'center', margin: '16px 0' }}>
              ✨ All caught up! No outstanding action items.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {sortedItems.map(item => {
                const overdue = isOverdue(item);
                const dueSoon = isDueSoon(item);
                const isAssignee = user.email.toLowerCase() === item.assigneeEmail.toLowerCase();
                const parentMeeting = meetings.find(m => m.meetingId === item.meetingId);

                return (
                  <div key={item.actionItemId} style={{
                    padding: '12px 16px', borderRadius: 14,
                    background: overdue ? 'rgba(220,38,38,0.04)' : dueSoon ? 'rgba(180,83,9,0.04)' : 'rgba(255,255,255,0.5)',
                    border: `1px solid ${overdue ? 'rgba(220,38,38,0.15)' : dueSoon ? 'rgba(180,83,9,0.15)' : 'rgba(180,160,210,0.15)'}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                    onClick={() => parentMeeting && onSelectMeeting(parentMeeting.meetingId)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          {overdue && <span style={{ fontSize: 10, color: '#dc2626', fontWeight: 700, textTransform: 'uppercase' }}>🔴 Overdue</span>}
                          {dueSoon && !overdue && <span style={{ fontSize: 10, color: '#b45309', fontWeight: 700, textTransform: 'uppercase' }}>🟡 Due Soon</span>}
                          <span style={{ fontSize: 10, color: PRIORITY_CONFIG[item.priority]?.color || '#6b7280', fontWeight: 600 }}>
                            ● {PRIORITY_CONFIG[item.priority]?.label || item.priority}
                          </span>
                        </div>
                        <p style={{ margin: '0 0 4px', fontSize: 14, color: '#3a2a50', fontWeight: 500 }}>{item.description}</p>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 11, color: '#8a7a9a' }}>
                          {parentMeeting && <span>From: {parentMeeting.title}</span>}
                          {item.dueDate && <span>· Due: {new Date(item.dueDate).toLocaleDateString()}</span>}
                          <span>· {isAssignee ? 'Yours' : item.assigneeName || item.assigneeRole}</span>
                        </div>
                        {item.rubricArea && (
                          <div style={{ marginTop: 4, fontSize: 11, color: '#7c4dba' }}>🌱 {item.rubricArea}</div>
                        )}
                      </div>
                      <div onClick={e => e.stopPropagation()}>
                        <ActionStatusDropdown
                          status={item.status}
                          canEdit={isAssignee}
                          onChange={(s) => handleStatusUpdate(item, s)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {closedItems.length > 0 && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(180,160,210,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#8a7a9a' }}>
                ✅ {closedItems.filter(i => i.status === 'completed').length} completed
                {closedItems.filter(i => i.status === 'deferred').length > 0 && ` · ⏸ ${closedItems.filter(i => i.status === 'deferred').length} deferred`}
              </span>
              <button onClick={() => setShowAllActions(!showAllActions)} style={{ ...btnSecondary, padding: '4px 12px', fontSize: 11 }}>
                {showAllActions ? 'Hide' : 'Show all'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Meetings List */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: '#3a2a50', fontSize: 18, fontFamily: "'Georgia', serif" }}>📅 Meetings</h3>
          <button onClick={onSchedule} style={btnPrimary}>+ Schedule Meeting</button>
        </div>

        {/* Filter pills */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {(['all', 'upcoming', 'past', 'pending'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer',
              background: filter === f ? 'linear-gradient(135deg, #7c4dba, #9b72cf)' : 'rgba(124,77,186,0.06)',
              color: filter === f ? '#fff' : '#7c4dba', fontSize: 12, fontWeight: 600,
              transition: 'all 0.2s', textTransform: 'capitalize',
            }}>
              {f}
            </button>
          ))}
        </div>

        {filteredMeetings.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📅</div>
            <p style={{ color: '#7a6a8a', fontSize: 14, margin: 0 }}>
              {meetings.length === 0 ? 'No meetings yet. Schedule your first meeting!' : 'No meetings match this filter.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filteredMeetings.map(m => {
              const meetingActions = allActionItems.filter(a => a.meetingId === m.meetingId);
              const overdueCount = meetingActions.filter(isOverdue).length;
              return (
                <button key={m.meetingId} onClick={() => onSelectMeeting(m.meetingId)} style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '16px 20px', borderRadius: 16, border: '1px solid rgba(180,160,210,0.15)',
                  background: 'rgba(255,255,255,0.5)', cursor: 'pointer', transition: 'all 0.2s',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#3a2a50', marginBottom: 4 }}>{m.title}</div>
                      <div style={{ fontSize: 13, color: '#7a6a8a', marginBottom: 4 }}>
                        with {m.organizerId === user.userId
                          ? `${m.inviteeRole === 'minister' ? 'Minister' : m.inviteeRole === 'sponsor' ? 'Sponsor' : 'Mentor'} (${m.inviteeName || m.inviteeEmail})`
                          : `${m.organizerRole === 'minister' ? 'Minister' : m.organizerRole === 'sponsor' ? 'Sponsor' : 'Mentor'} (${m.organizerName})`
                        }
                      </div>
                      <div style={{ fontSize: 12, color: '#9a8ab0' }}>
                        {formatDateTime(m.dateTime)} · {formatDuration(m.duration)}
                      </div>
                      {meetingActions.length > 0 && (
                        <div style={{ fontSize: 11, color: overdueCount > 0 ? '#dc2626' : '#7c4dba', marginTop: 4 }}>
                          📋 {meetingActions.length} action item{meetingActions.length !== 1 ? 's' : ''}
                          {overdueCount > 0 && ` (${overdueCount} overdue)`}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={m.status} />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

// =============================================================================
// Schedule Meeting Form
// =============================================================================

function ScheduleMeetingForm({ reportId, user, role, data, onBack, onCreated }: {
  reportId: string;
  user: User;
  role: ReportRole;
  data: Record<string, string>;
  onBack: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    inviteeRole: role === 'minister' ? 'sponsor' : 'minister' as ReportRole,
    title: '',
    dateTime: '',
    duration: 60,
    location: '',
    agenda: '',
    expectedOutcomes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const suggestedTitles = ['Monthly Check-in', 'Quarterly Review', 'Goal Setting', 'Progress Discussion'];

  // Determine available invitee roles
  const inviteeOptions: { value: ReportRole; label: string; email: string; timezone: string }[] = [];
  if (role !== 'minister') {
    inviteeOptions.push({ value: 'minister', label: 'Minister', email: data.email || '', timezone: '' });
  }
  if (role !== 'sponsor' && data.sponsorEmail) {
    inviteeOptions.push({ value: 'sponsor', label: 'Sponsor', email: data.sponsorEmail, timezone: data.sponsorTimezone || '' });
  }
  if (role !== 'mentor' && data.mentorEmail) {
    inviteeOptions.push({ value: 'mentor', label: 'Mentor', email: data.mentorEmail, timezone: data.mentorTimezone || '' });
  }

  const handleSubmit = async () => {
    if (!form.title || !form.dateTime) {
      setErr('Please fill in the title and date/time.');
      return;
    }
    setSubmitting(true);
    setErr(null);
    const selectedInvitee = inviteeOptions.find(o => o.value === form.inviteeRole);
    try {
      const res = await fetch(`/api/reports/${reportId}/meetings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...form, role, timezone: selectedInvitee?.timezone || '' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to schedule meeting');
      }
      onCreated();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={cardStyle}>
      <button onClick={onBack} style={{ ...btnSecondary, marginBottom: 20, padding: '6px 14px', fontSize: 12 }}>
        ← Back to Meetings
      </button>

      <h3 style={{ margin: '0 0 4px', color: '#3a2a50', fontSize: 20, fontFamily: "'Georgia', serif" }}>Schedule a Meeting</h3>
      <p style={{ margin: '0 0 24px', color: '#7a6a8a', fontSize: 13, fontStyle: 'italic' }}>
        Set up a time to connect with your sponsor or mentor.
      </p>

      {/* Meeting With */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Meeting With</label>
        <select
          value={form.inviteeRole}
          onChange={e => setForm(f => ({ ...f, inviteeRole: e.target.value as ReportRole }))}
          style={{ ...inputStyle, cursor: 'pointer' }}
        >
          {inviteeOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label} ({opt.email})</option>
          ))}
        </select>
      </div>

      {/* Title */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Title</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          {suggestedTitles.map(t => (
            <button key={t} onClick={() => setForm(f => ({ ...f, title: t }))} style={{
              padding: '4px 12px', borderRadius: 14, border: '1px solid rgba(124,77,186,0.2)',
              background: form.title === t ? 'rgba(124,77,186,0.1)' : 'transparent',
              color: '#7c4dba', fontSize: 11, cursor: 'pointer', transition: 'all 0.2s',
            }}>
              {t}
            </button>
          ))}
        </div>
        <input
          value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          placeholder="Meeting title"
          style={inputStyle}
        />
      </div>

      {/* Date/Time and Duration */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={labelStyle}>Date</label>
          <input
            type="date"
            value={form.dateTime ? form.dateTime.split('T')[0] : ''}
            onChange={e => {
              const time = form.dateTime ? form.dateTime.split('T')[1] || '10:00' : '10:00';
              setForm(f => ({ ...f, dateTime: `${e.target.value}T${time}` }));
            }}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Time</label>
          <input
            type="time"
            value={form.dateTime ? (form.dateTime.split('T')[1] || '10:00') : ''}
            onChange={e => {
              const date = form.dateTime ? form.dateTime.split('T')[0] : new Date().toISOString().split('T')[0];
              setForm(f => ({ ...f, dateTime: `${date}T${e.target.value}` }));
            }}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Duration</label>
          <select
            value={form.duration}
            onChange={e => setForm(f => ({ ...f, duration: parseInt(e.target.value) }))}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            <option value={30}>30 minutes</option>
            <option value={45}>45 minutes</option>
            <option value={60}>1 hour</option>
            <option value={90}>1.5 hours</option>
            <option value={120}>2 hours</option>
          </select>
        </div>
      </div>

      {/* Timezone awareness indicator */}
      {(() => {
        const selectedInvitee = inviteeOptions.find(o => o.value === form.inviteeRole);
        const inviteeTz = selectedInvitee?.timezone;
        const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const tzLabel = (tz: string) => {
          try {
            const short = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
              .formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value || tz;
            return short;
          } catch { return tz; }
        };

        if (inviteeTz && inviteeTz !== localTz) {
          // Show timezone conversion
          let inviteeTimeStr = '';
          if (form.dateTime) {
            try {
              // form.dateTime is in the organizer's local time (e.g., "2026-06-15T14:00")
              // Create a date in the organizer's local time then format in invitee's timezone
              const localDate = new Date(form.dateTime);
              if (!isNaN(localDate.getTime())) {
                inviteeTimeStr = new Intl.DateTimeFormat('en-US', {
                  timeZone: inviteeTz, weekday: 'short', month: 'short', day: 'numeric',
                  hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
                }).format(localDate);
              }
            } catch { /* invalid tz */ }
          }

          return (
            <div style={{
              padding: '10px 16px', borderRadius: 12, marginBottom: 20,
              background: 'linear-gradient(135deg, rgba(37,99,235,0.04), rgba(59,130,246,0.08))',
              border: '1px solid rgba(37,99,235,0.12)',
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: 16 }}>🌍</span>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 12, color: '#2563eb', fontWeight: 600, marginBottom: 1 }}>
                  {selectedInvitee?.label}'s timezone: {tzLabel(inviteeTz)}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  {inviteeTimeStr
                    ? <>This meeting will be <strong style={{ color: '#2563eb' }}>{inviteeTimeStr}</strong> for them</>
                    : <>Select a date/time to see the conversion</>
                  }
                </div>
                <div style={{ fontSize: 11, color: '#9a8ab0', marginTop: 2 }}>
                  Your timezone: {tzLabel(localTz)}
                </div>
              </div>
            </div>
          );
        } else if (inviteeTz && inviteeTz === localTz) {
          return (
            <div style={{
              padding: '8px 16px', borderRadius: 12, marginBottom: 20,
              background: 'rgba(5,150,105,0.04)', border: '1px solid rgba(5,150,105,0.1)',
              fontSize: 12, color: '#059669',
            }}>
              ✅ You and your {selectedInvitee?.label?.toLowerCase()} are in the same timezone ({tzLabel(inviteeTz)})
            </div>
          );
        }
        return inviteeTz ? null : (
          <div style={{
            padding: '8px 16px', borderRadius: 12, marginBottom: 20,
            background: 'rgba(180,83,9,0.04)', border: '1px solid rgba(180,83,9,0.1)',
            fontSize: 12, color: '#b45309',
          }}>
            💡 Tip: Set your {selectedInvitee?.label?.toLowerCase()}'s timezone on the Minister Info tab to see time conversions when scheduling.
          </div>
        );
      })()}

      {/* Location */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Location / Video Link <span style={{ fontWeight: 400, color: '#9a8ab0' }}>(optional)</span></label>
        <input
          value={form.location}
          onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
          placeholder="Zoom link, phone number, or address"
          style={inputStyle}
        />
      </div>

      {/* Agenda */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Agenda</label>
        <textarea
          value={form.agenda}
          onChange={e => setForm(f => ({ ...f, agenda: e.target.value }))}
          placeholder={"What topics would you like to discuss?\n• Ministry progress\n• Challenges and support needed\n• Goals for next period\n• Questions for sponsor/mentor"}
          style={textareaStyle}
          rows={5}
        />
      </div>

      {/* Expected Outcomes */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Expected Outcomes</label>
        <textarea
          value={form.expectedOutcomes}
          onChange={e => setForm(f => ({ ...f, expectedOutcomes: e.target.value }))}
          placeholder="What do you hope to accomplish in this meeting?"
          style={textareaStyle}
          rows={3}
        />
      </div>

      {err && (
        <div style={{ padding: '10px 14px', borderRadius: 12, background: 'rgba(220,38,38,0.05)', border: '1px solid rgba(220,38,38,0.2)', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>
          {err}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={handleSubmit} disabled={submitting} style={{ ...btnPrimary, opacity: submitting ? 0.6 : 1 }}>
          {submitting ? '⏳ Sending...' : '📧 Send Invitation'}
        </button>
        <button onClick={onBack} style={btnSecondary}>Cancel</button>
      </div>
    </div>
  );
}

// =============================================================================
// Meeting Detail View
// =============================================================================

function MeetingDetail({ meetingId, user, role, reportId, onBack, onRefresh }: {
  meetingId: string;
  user: User;
  role: ReportRole;
  reportId: string;
  onBack: () => void;
  onRefresh: () => void;
}) {
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAISuggest, setShowAISuggest] = useState(false);
  const [aiResult, setAiResult] = useState<AISuggestResponse | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [editingFields, setEditingFields] = useState<Partial<Meeting>>({});
  const saveTimerRef = useRef<number | null>(null);

  const loadMeeting = useCallback(async () => {
    try {
      setLoading(true);
      const [meetingRes, actionsRes] = await Promise.all([
        fetch(`/api/meetings/${meetingId}`, { credentials: 'include' }),
        fetch(`/api/meetings/${meetingId}/action-items`, { credentials: 'include' }),
      ]);
      if (meetingRes.ok) {
        const { meeting: m } = await meetingRes.json();
        setMeeting(m);
      }
      if (actionsRes.ok) {
        const { actionItems: a } = await actionsRes.json();
        setActionItems(a || []);
      }
    } catch (err) {
      console.error('Failed to load meeting:', err);
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  useEffect(() => { loadMeeting(); }, [loadMeeting]);

  // Debounced auto-save for meeting content fields
  const autoSave = useCallback((fields: Partial<Meeting>) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(async () => {
      try {
        await fetch(`/api/meetings/${meetingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(fields),
        });
      } catch (err) {
        console.error('Auto-save failed:', err);
      }
    }, 2000);
  }, [meetingId]);

  const updateField = (field: keyof Meeting, value: string) => {
    setMeeting(prev => prev ? { ...prev, [field]: value } : null);
    const updated = { ...editingFields, [field]: value };
    setEditingFields(updated);
    autoSave(updated);
  };

  // Meeting actions
  const handleAction = async (action: string, body?: object) => {
    try {
      const res = await fetch(`/api/meetings/${meetingId}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: body ? JSON.stringify(body) : undefined,
      });
      if (res.ok) {
        loadMeeting();
        onRefresh();
      }
    } catch (err) {
      console.error(`Failed to ${action} meeting:`, err);
    }
  };

  // Download ICS
  const downloadICS = async () => {
    try {
      const res = await fetch(`/api/meetings/${meetingId}/calendar`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${meeting?.title || 'meeting'}.ics`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Failed to download ICS:', err);
    }
  };

  // AI Suggest
  const handleAISuggest = async () => {
    setAiLoading(true);
    setShowAISuggest(true);
    try {
      const res = await fetch(`/api/meetings/${meetingId}/ai-suggest`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setAiResult(data);
      } else {
        const err = await res.json().catch(() => ({}));
        console.error('AI suggest error:', err);
      }
    } catch (err) {
      console.error('AI suggest failed:', err);
    } finally {
      setAiLoading(false);
    }
  };

  // Accept AI suggestion
  const acceptSuggestion = async (suggestion: AISuggestion, edits?: Partial<AISuggestion>) => {
    const merged = { ...suggestion, ...edits };
    try {
      await fetch(`/api/meetings/${meetingId}/action-items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          description: merged.description,
          assigneeRole: merged.assigneeRole,
          priority: merged.priority,
          rubricArea: merged.rubricArea,
          aiGenerated: true,
          assigneeEmail: merged.assigneeRole === role ? user.email : '',
          assigneeName: merged.assigneeRole === role ? user.name : '',
        }),
      });
      loadMeeting();
    } catch (err) {
      console.error('Failed to accept suggestion:', err);
    }
  };

  // Action item status update
  const handleItemStatusUpdate = async (item: ActionItem, newStatus: ActionItemStatus) => {
    try {
      await fetch(`/api/meetings/${item.meetingId}/action-items/${item.actionItemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      loadMeeting();
    } catch (err) {
      console.error('Failed to update action item:', err);
    }
  };

  // Delete action item
  const handleDeleteItem = async (item: ActionItem) => {
    if (!confirm('Delete this action item?')) return;
    try {
      await fetch(`/api/meetings/${item.meetingId}/action-items/${item.actionItemId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      loadMeeting();
    } catch (err) {
      console.error('Failed to delete action item:', err);
    }
  };

  if (loading || !meeting) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <p style={{ color: '#7a6a8a', fontSize: 15 }}>Loading meeting details...</p>
      </div>
    );
  }

  const isOrganizer = meeting.organizerId === user.userId;
  const isInvitee = user.email.toLowerCase() === meeting.inviteeEmail.toLowerCase();
  const isPast = new Date(meeting.dateTime) < new Date();

  return (
    <div>
      {/* Back + Header */}
      <button onClick={onBack} style={{ ...btnSecondary, marginBottom: 16, padding: '6px 14px', fontSize: 12 }}>
        ← All Meetings
      </button>

      <div style={{ ...cardStyle, background: watercolorBg(250) }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: '0 0 4px', color: '#3a2a50', fontSize: 22, fontFamily: "'Georgia', serif" }}>{meeting.title}</h3>
            <div style={{ fontSize: 13, color: '#7a6a8a' }}>
              {isOrganizer ? 'You invited' : 'Invited by'}{' '}
              <strong>{isOrganizer ? (meeting.inviteeName || meeting.inviteeEmail) : meeting.organizerName}</strong>
              {' '}({isOrganizer ? meeting.inviteeRole : meeting.organizerRole})
            </div>
          </div>
          <StatusBadge status={meeting.status} />
        </div>

        {/* Meeting details row */}
        <div style={{
          display: 'flex', gap: 16, flexWrap: 'wrap', padding: '12px 16px',
          background: 'rgba(255,255,255,0.4)', borderRadius: 12, marginBottom: 16, fontSize: 13, color: '#5a4a6a',
        }}>
          <span>📅 {formatDateTime(meeting.dateTime)}</span>
          <span>⏱ {formatDuration(meeting.duration)}</span>
          {meeting.location && <span>📍 {meeting.location}</span>}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {isInvitee && meeting.status === 'pending' && (
            <>
              <button onClick={() => handleAction('accept')} style={{ ...btnPrimary, background: 'linear-gradient(135deg, #059669, #10b981)' }}>✅ Accept</button>
              <button onClick={() => handleAction('decline')} style={btnDanger}>❌ Decline</button>
            </>
          )}
          {isOrganizer && meeting.status === 'pending' && (
            <>
              <button onClick={() => handleAction('remind')} style={btnSecondary}>📧 Send Reminder</button>
              <button onClick={() => handleAction('cancel')} style={btnDanger}>🚫 Cancel Meeting</button>
            </>
          )}
          {meeting.status === 'accepted' && isPast && (
            <button onClick={() => handleAction('complete')} style={{ ...btnPrimary, background: 'linear-gradient(135deg, #2563eb, #3b82f6)' }}>✅ Mark Completed</button>
          )}
          {meeting.status !== 'cancelled' && meeting.status !== 'declined' && (
            <button onClick={downloadICS} style={btnSecondary}>📥 Download .ics</button>
          )}
        </div>
      </div>

      {/* Pre-Meeting: Agenda & Expected Outcomes */}
      <div style={cardStyle}>
        <h4 style={{ margin: '0 0 16px', color: '#3a2a50', fontSize: 16, fontFamily: "'Georgia', serif" }}>📋 Pre-Meeting</h4>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Agenda</label>
          <textarea
            value={meeting.agenda || ''}
            onChange={e => updateField('agenda', e.target.value)}
            placeholder={"What topics to discuss?\n• Ministry progress\n• Challenges and support needed\n• Goals for next period"}
            style={textareaStyle}
            rows={4}
          />
        </div>

        <div>
          <label style={labelStyle}>Expected Outcomes</label>
          <textarea
            value={meeting.expectedOutcomes || ''}
            onChange={e => updateField('expectedOutcomes', e.target.value)}
            placeholder="What do you hope to accomplish in this meeting?"
            style={textareaStyle}
            rows={3}
          />
        </div>
      </div>

      {/* Post-Meeting: Notes & Outcomes (visible when completed) */}
      {meeting.status === 'completed' && (
        <div style={{ ...cardStyle, background: watercolorBg(140) }}>
          <h4 style={{ margin: '0 0 16px', color: '#3a2a50', fontSize: 16, fontFamily: "'Georgia', serif" }}>📝 Post-Meeting Notes</h4>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Meeting Notes</label>
            <textarea
              value={meeting.notes || ''}
              onChange={e => updateField('notes', e.target.value)}
              placeholder={"Key Discussion Points:\n• \n\nDecisions Made:\n• \n\nInsights & Reflections:\n• "}
              style={textareaStyle}
              rows={8}
            />
          </div>

          <div>
            <label style={labelStyle}>Actual Outcomes</label>
            <textarea
              value={meeting.actualOutcomes || ''}
              onChange={e => updateField('actualOutcomes', e.target.value)}
              placeholder="What was achieved in this meeting?"
              style={textareaStyle}
              rows={3}
            />
          </div>
        </div>
      )}

      {/* Action Items (visible when accepted or completed) */}
      {(meeting.status === 'accepted' || meeting.status === 'completed') && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h4 style={{ margin: 0, color: '#3a2a50', fontSize: 16, fontFamily: "'Georgia', serif" }}>📋 Action Items</h4>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleAISuggest} style={{ ...btnSecondary, background: 'rgba(124,77,186,0.06)' }}>
                ✨ AI Suggest
              </button>
              <button onClick={() => setShowAddItem(true)} style={btnSecondary}>+ Add Item</button>
            </div>
          </div>

          {/* Action items list */}
          {actionItems.length === 0 && !showAddItem && !showAISuggest ? (
            <p style={{ color: '#7a6a8a', fontSize: 14, fontStyle: 'italic', textAlign: 'center', margin: '20px 0' }}>
              No action items yet. Add items or use AI to suggest items based on the minister's growth journey.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {actionItems.map(item => {
                const isAssignee = user.email.toLowerCase() === item.assigneeEmail.toLowerCase();
                return (
                  <div key={item.actionItemId} style={{
                    padding: '12px 16px', borderRadius: 14,
                    background: 'rgba(255,255,255,0.5)', border: '1px solid rgba(180,160,210,0.15)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <span style={{ fontSize: 11, color: PRIORITY_CONFIG[item.priority]?.color, fontWeight: 600 }}>
                            ● {PRIORITY_CONFIG[item.priority]?.label}
                          </span>
                        </div>
                        <p style={{ margin: '0 0 4px', fontSize: 14, color: '#3a2a50', fontWeight: 500 }}>{item.description}</p>
                        <div style={{ fontSize: 11, color: '#8a7a9a', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <span>👤 {item.assigneeName || item.assigneeRole} {isAssignee ? '(You)' : ''}</span>
                          {item.dueDate && <span>· 📅 Due: {new Date(item.dueDate).toLocaleDateString()}</span>}
                        </div>
                        {item.rubricArea && (
                          <div style={{ marginTop: 4, fontSize: 11, color: '#7c4dba' }}>🌱 {item.rubricArea}</div>
                        )}
                        {item.notes && (
                          <div style={{ marginTop: 4, fontSize: 12, color: '#5a4a6a', fontStyle: 'italic' }}>"{item.notes}"</div>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                        <ActionStatusDropdown
                          status={item.status}
                          canEdit={isAssignee}
                          onChange={(s) => handleItemStatusUpdate(item, s)}
                        />
                        <button onClick={() => handleDeleteItem(item)} style={{
                          background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#b0a0c0',
                          padding: '2px 4px',
                        }}>🗑</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Action Item Form */}
          {showAddItem && (
            <AddActionItemForm
              meetingId={meetingId}
              meeting={meeting}
              user={user}
              role={role}
              onAdded={() => { setShowAddItem(false); loadMeeting(); }}
              onCancel={() => setShowAddItem(false)}
            />
          )}

          {/* AI Suggestions Panel */}
          {showAISuggest && (
            <AISuggestPanel
              loading={aiLoading}
              result={aiResult}
              onAccept={acceptSuggestion}
              onDismiss={() => setShowAISuggest(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Add Action Item Form
// =============================================================================

function AddActionItemForm({ meetingId, meeting, user, role, onAdded, onCancel }: {
  meetingId: string;
  meeting: Meeting;
  user: User;
  role: ReportRole;
  onAdded: () => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    description: '',
    assigneeRole: role as ReportRole,
    priority: 'medium' as ActionItemPriority,
    dueDate: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Determine assignee email based on role
  const getAssigneeEmail = (r: ReportRole): string => {
    if (r === role) return user.email;
    if (r === meeting.organizerRole) return meeting.organizerEmail;
    return meeting.inviteeEmail;
  };

  const getAssigneeName = (r: ReportRole): string => {
    if (r === role) return user.name || user.email;
    if (r === meeting.organizerRole) return meeting.organizerName;
    return meeting.inviteeName || meeting.inviteeEmail;
  };

  const handleSubmit = async () => {
    if (!form.description.trim()) return;
    setSubmitting(true);
    try {
      await fetch(`/api/meetings/${meetingId}/action-items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...form,
          assigneeEmail: getAssigneeEmail(form.assigneeRole),
          assigneeName: getAssigneeName(form.assigneeRole),
        }),
      });
      onAdded();
    } catch (err) {
      console.error('Failed to add action item:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Available roles: only the two meeting participants
  const participantRoles = [meeting.organizerRole, meeting.inviteeRole];

  return (
    <div style={{
      marginTop: 12, padding: '16px 20px', borderRadius: 14,
      background: 'rgba(124,77,186,0.03)', border: '1px solid rgba(124,77,186,0.12)',
    }}>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Description</label>
        <input
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="What needs to be done?"
          style={inputStyle}
          autoFocus
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={labelStyle}>Assign to</label>
          <select
            value={form.assigneeRole}
            onChange={e => setForm(f => ({ ...f, assigneeRole: e.target.value as ReportRole }))}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            {participantRoles.map(r => (
              <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)} {r === role ? '(You)' : ''}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Priority</label>
          <div style={{ display: 'flex', gap: 4 }}>
            {(['low', 'medium', 'high'] as const).map(p => (
              <button key={p} onClick={() => setForm(f => ({ ...f, priority: p }))} style={{
                flex: 1, padding: '8px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: form.priority === p ? PRIORITY_CONFIG[p].color : 'rgba(180,160,210,0.1)',
                color: form.priority === p ? '#fff' : '#7a6a8a', fontSize: 11, fontWeight: 600,
                transition: 'all 0.2s',
              }}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label style={labelStyle}>Due Date <span style={{ fontWeight: 400, color: '#9a8ab0' }}>(opt.)</span></label>
          <input
            type="date"
            value={form.dueDate}
            onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
            style={inputStyle}
          />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Notes <span style={{ fontWeight: 400, color: '#9a8ab0' }}>(optional)</span></label>
        <input
          value={form.notes}
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          placeholder="Additional context"
          style={inputStyle}
        />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleSubmit} disabled={submitting || !form.description.trim()} style={{ ...btnPrimary, fontSize: 13, padding: '8px 20px', opacity: submitting ? 0.6 : 1 }}>
          {submitting ? '⏳ Adding...' : 'Add Action Item'}
        </button>
        <button onClick={onCancel} style={{ ...btnSecondary, fontSize: 13 }}>Cancel</button>
      </div>
    </div>
  );
}

// =============================================================================
// AI Suggestions Panel
// =============================================================================

function AISuggestPanel({ loading, result, onAccept, onDismiss }: {
  loading: boolean;
  result: AISuggestResponse | null;
  onAccept: (suggestion: AISuggestion) => void;
  onDismiss: () => void;
}) {
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());
  const [accepted, setAccepted] = useState<Set<number>>(new Set());

  if (loading) {
    return (
      <div style={{
        marginTop: 16, padding: '32px 20px', borderRadius: 14,
        background: 'linear-gradient(135deg, rgba(124,77,186,0.04), rgba(155,114,207,0.08))',
        border: '1px solid rgba(124,77,186,0.15)', textAlign: 'center',
      }}>
        <div style={{ fontSize: 32, marginBottom: 12, animation: 'pulse 2s ease-in-out infinite' }}>✨</div>
        <p style={{ color: '#7c4dba', fontSize: 14, fontWeight: 600, margin: '0 0 4px' }}>Analyzing ministerial growth journey...</p>
        <p style={{ color: '#9a8ab0', fontSize: 12, margin: 0 }}>Reviewing reports across multiple years against the consciousness rubric</p>
      </div>
    );
  }

  if (!result) {
    return (
      <div style={{ marginTop: 16, padding: '20px', borderRadius: 14, background: 'rgba(220,38,38,0.04)', border: '1px solid rgba(220,38,38,0.15)', textAlign: 'center' }}>
        <p style={{ color: '#dc2626', fontSize: 13, margin: 0 }}>Unable to generate suggestions. Please try again.</p>
        <button onClick={onDismiss} style={{ ...btnSecondary, marginTop: 12, fontSize: 12 }}>Dismiss</button>
      </div>
    );
  }

  const { assessment, suggestedItems } = result;
  const remainingItems = suggestedItems.filter((_, i) => !dismissed.has(i) && !accepted.has(i));

  return (
    <div style={{
      marginTop: 16, padding: '24px 20px', borderRadius: 16,
      background: 'linear-gradient(135deg, rgba(124,77,186,0.04), rgba(155,114,207,0.08))',
      border: '1px solid rgba(124,77,186,0.15)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <h4 style={{ margin: 0, color: '#3a2a50', fontSize: 16, fontFamily: "'Georgia', serif" }}>✨ AI-Suggested Action Items</h4>
        <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#8a7a9a', padding: 4 }}>✕</button>
      </div>

      {/* Assessment Card */}
      {assessment && (
        <div style={{
          padding: '16px 20px', borderRadius: 14, marginBottom: 16,
          background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(180,160,210,0.2)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#3a2a50', textTransform: 'uppercase', letterSpacing: 1 }}>
              Current Level: {assessment.level}
            </span>
            <span style={{
              padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
              background: 'rgba(124,77,186,0.1)', color: '#7c4dba',
            }}>Rating {assessment.rating}/5</span>
          </div>

          {/* Progress bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 10, color: '#8a7a9a', width: 70 }}>Formation</span>
            <div style={{ flex: 1, height: 6, background: 'rgba(180,160,210,0.15)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 3, transition: 'width 0.6s ease',
                background: 'linear-gradient(90deg, #7c4dba, #9b72cf, #c8a2f0)',
                width: `${(assessment.rating / 5) * 100}%`,
              }} />
            </div>
            <span style={{ fontSize: 10, color: '#8a7a9a', width: 80, textAlign: 'right' }}>Embodiment</span>
          </div>

          <p style={{ margin: '0 0 4px', fontSize: 13, color: '#5a4a6a', lineHeight: 1.6 }}>{assessment.evidence}</p>
          {assessment.trajectory && (
            <p style={{ margin: 0, fontSize: 12, color: '#7c4dba', fontStyle: 'italic' }}>📈 Trajectory: {assessment.trajectory}</p>
          )}
        </div>
      )}

      {/* Suggestion Cards */}
      {suggestedItems.map((suggestion, idx) => {
        if (dismissed.has(idx) || accepted.has(idx)) return null;
        return (
          <div key={idx} style={{
            padding: '16px 20px', borderRadius: 14, marginBottom: 10,
            background: 'rgba(255,255,255,0.5)', border: '1px solid rgba(180,160,210,0.15)',
          }}>
            <p style={{ margin: '0 0 8px', fontSize: 14, color: '#3a2a50', fontWeight: 500 }}>{suggestion.description}</p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8, fontSize: 11, color: '#7a6a8a' }}>
              <span>Assign: <strong style={{ color: '#5a4a6a' }}>{suggestion.assigneeRole}</strong></span>
              <span>Priority: <strong style={{ color: PRIORITY_CONFIG[suggestion.priority]?.color }}>{suggestion.priority}</strong></span>
              <span style={{ color: '#7c4dba' }}>🌱 {suggestion.rubricArea}</span>
              {suggestion.suggestedTimeline && <span>⏱ {suggestion.suggestedTimeline}</span>}
            </div>
            <p style={{ margin: '0 0 10px', fontSize: 12, color: '#5a4a6a', lineHeight: 1.5, fontStyle: 'italic' }}>
              💡 {suggestion.reasoning}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { onAccept(suggestion); setAccepted(prev => new Set(prev).add(idx)); }} style={{ ...btnPrimary, fontSize: 12, padding: '6px 14px' }}>
                ✅ Accept
              </button>
              <button onClick={() => setDismissed(prev => new Set(prev).add(idx))} style={{ ...btnSecondary, fontSize: 12, padding: '6px 14px' }}>
                ✕ Dismiss
              </button>
            </div>
          </div>
        );
      })}

      {remainingItems.length > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={() => {
            remainingItems.forEach((s, i) => {
              const origIdx = suggestedItems.indexOf(s);
              onAccept(s);
              setAccepted(prev => new Set(prev).add(origIdx));
            });
          }} style={{ ...btnPrimary, fontSize: 12, padding: '6px 14px' }}>
            Accept All ({remainingItems.length})
          </button>
          <button onClick={onDismiss} style={{ ...btnSecondary, fontSize: 12 }}>Dismiss All</button>
        </div>
      )}

      {remainingItems.length === 0 && (
        <p style={{ color: '#059669', fontSize: 13, textAlign: 'center', margin: '16px 0 0', fontWeight: 600 }}>
          ✅ All suggestions have been processed!
        </p>
      )}
    </div>
  );
}
